import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAksCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import type { AksRequest, AksNodePoolInput } from '../../api/types'
import { TrustBar } from '../shared/TrustBar'
import { ShareButton } from '../shared/ShareButton'
import { FieldTooltip } from '../shared/FieldTooltip'
import { ScenarioPresets } from '../shared/ScenarioPresets'

const VM_SKUS = ['D2s v3', 'D4s v3', 'D8s v3', 'B2ms', 'B4ms']

const TIER_LABELS: Record<string, string> = {
  free: 'Free (dev/test)',
  standard: 'Standard (production SLA)',
  premium: 'Premium (long-term support)',
}

type Tier = 'free' | 'standard' | 'premium'

function fmt(n: number) {
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function SelectField({ label, value, options, onChange, hint, tooltipContent }: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  hint?: string
  tooltipContent?: string
}) {
  return (
    <div>
      <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
        {label}{tooltipContent && <FieldTooltip content={tooltipContent} />}
      </label>
      {hint && <p className="text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded px-3 py-2 text-[13px] border"
        style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-primary)' }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

function NumInput({ label, value, onChange, min = 0, max, step = 1, hint }: {
  label: string; value: number; onChange: (v: number) => void
  min?: number; max?: number; step?: number; hint?: string
}) {
  return (
    <div>
      <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{label}</label>
      {hint && <p className="text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      <input
        type="number" value={value} min={min} max={max} step={step}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
        className="w-full rounded px-3 py-2 text-[13px] border"
        style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-primary)' }}
      />
    </div>
  )
}

function Toggle({ label, checked, onChange, disabled, hint, tooltipContent }: {
  label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; hint?: string; tooltipContent?: string
}) {
  return (
    <label className={`flex items-start gap-3 cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <div className="relative mt-0.5">
        <input
          type="checkbox" checked={checked} disabled={disabled}
          onChange={(e) => !disabled && onChange(e.target.checked)}
          className="sr-only"
        />
        <div
          className="w-10 h-6 rounded-full transition-colors"
          style={{ backgroundColor: checked && !disabled ? '#0078d4' : 'var(--outline-variant)' }}
        />
        <div
          className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform"
          style={{ transform: checked && !disabled ? 'translateX(16px)' : 'translateX(0)' }}
        />
      </div>
      <div>
        <span className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>{label}{tooltipContent && <FieldTooltip content={tooltipContent} />}</span>
        {hint && <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      </div>
    </label>
  )
}

const DEFAULT_POOLS: AksNodePoolInput[] = [
  { sku: 'D2s v3', nodeCount: 3, spot: false },
]

export default function AksCalc() {
  const [tier, setTier] = useState<Tier>('standard')
  const [uptimeSla, setUptimeSla] = useState(false)
  const [pools, setPools] = useState<AksNodePoolInput[]>(DEFAULT_POOLS)
  const [egressGb, setEgressGb] = useState(0)

  const inputs: AksRequest = {
    controlPlaneTier: tier,
    uptimeSlaEnabled: uptimeSla,
    nodePools: pools,
    monthlyEgressGb: egressGb,
  }

  const { data: result, isLoading, isError } = useAksCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()

  useEffect(() => {
    if (result?.totalMonthly) {
      trackCalculatorUsed({ calculator: 'aks', result_gbp: result.totalMonthly, tier })
    }
  }, [result?.totalMonthly])

  function addPool() {
    if (pools.length >= 10) return
    setPools([...pools, { sku: 'D2s v3', nodeCount: 2, spot: false }])
  }

  function removePool(i: number) {
    if (pools.length <= 1) return
    setPools(pools.filter((_, idx) => idx !== i))
  }

  function updatePool(i: number, patch: Partial<AksNodePoolInput>) {
    setPools(pools.map((p, idx) => idx === i ? { ...p, ...patch } : p))
  }

  function handleTierChange(t: string) {
    const newTier = t as Tier
    setTier(newTier)
    if (newTier === 'free') setUptimeSla(false)
  }

  return (
    <div className="space-y-6">
      <TrustBar />

      <ScenarioPresets presets={[
        {
          label: 'Dev cluster',
          description: 'Free tier · 2× D2s v3 nodes · no SLA — control plane is free',
          apply: () => { setTier('free'); setUptimeSla(false); setPools([{ sku: 'D2s v3', nodeCount: 2, spot: false }]); },
        },
        {
          label: 'Production cluster',
          description: 'Standard · system pool (3× D2s v3) + user pool (3× D4s v3) · SLA',
          apply: () => { setTier('standard'); setUptimeSla(true); setPools([{ sku: 'D2s v3', nodeCount: 3, spot: false }, { sku: 'D4s v3', nodeCount: 3, spot: false }]); },
        },
        {
          label: 'Cost-optimised (Spot)',
          description: 'Standard · system pool on-demand + user pool on Spot — up to 90% saving',
          apply: () => { setTier('standard'); setUptimeSla(false); setPools([{ sku: 'D2s v3', nodeCount: 3, spot: false }, { sku: 'D4s v3', nodeCount: 5, spot: true }]); },
        },
      ]} />

      {/* Section 1: Cluster configuration */}
      <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Cluster configuration</h2>

        <SelectField
          label="Control plane tier"
          tooltipContent="Free: no SLA, dev/test only. Standard: 99.9% uptime SLA. Premium: adds long-term support and enhanced features."
          value={tier}
          onChange={handleTierChange}
          options={Object.entries(TIER_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          hint="Standard tier costs £55.10/month per cluster. Premium adds long-term support."
        />

        <Toggle
          label="Uptime SLA"
          tooltipContent="Guarantees 99.95% API server uptime. Roughly doubles the control plane cost but required for production workloads."
          checked={uptimeSla}
          onChange={setUptimeSla}
          disabled={tier === 'free'}
          hint={
            tier === 'free'
              ? 'Uptime SLA requires Standard or Premium tier'
              : 'Additional £55.10/month on top of Standard/Premium control plane fee'
          }
        />
      </div>

      {/* Section 2: Node pools */}
      <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <div className="flex items-center justify-between">
          <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Node pools</h2>
          {pools.length < 10 && (
            <button
              onClick={addPool}
              className="flex items-center gap-1 text-[12px] px-3 py-1.5 rounded transition-colors"
              style={{ backgroundColor: 'var(--surface-container-high)', color: '#0078d4', border: '1px solid var(--outline-variant)' }}
            >
              <span className="material-symbols-outlined text-[14px]">add</span>
              Add pool
            </button>
          )}
        </div>

        {pools.map((pool, i) => (
          <div key={i} className="p-4 rounded space-y-3" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>Pool {i + 1}</span>
              {pools.length > 1 && (
                <button onClick={() => removePool(i)} className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                  <span className="material-symbols-outlined text-[16px]">close</span>
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SelectField
                label="VM SKU"
                tooltipContent="SKU = VM size. D-series: general purpose. B-series: burstable, cheaper for variable workloads. More vCPU/RAM = higher cost."
                value={pool.sku}
                onChange={(v) => updatePool(i, { sku: v })}
                options={VM_SKUS.map(s => ({ value: s, label: s }))}
              />
              <NumInput
                label="Node count"
                value={pool.nodeCount}
                onChange={(v) => updatePool(i, { nodeCount: Math.min(1000, Math.max(1, v)) })}
                min={1}
                max={1000}
              />
            </div>

            <Toggle
              label="Spot nodes"
              checked={!!pool.spot}
              onChange={(v) => updatePool(i, { spot: v })}
              hint="~70% discount vs Pay-as-you-go. Azure can evict with 30s notice."
            />
          </div>
        ))}
      </div>

      {/* Section 3: Egress */}
      <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Egress &amp; Load Balancer</h2>
        <NumInput
          label="Monthly outbound data (GB)"
          value={egressGb}
          onChange={setEgressGb}
          min={0}
          step={10}
          hint="AKS provisions a Standard Load Balancer by default. Outbound data processing charges at £0.0037/GB."
        />
      </div>

      {/* Result panel */}
      {isLoading && (
        <div className="p-5 rounded-lg text-center text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
          Calculating…
        </div>
      )}
      {isError && (
        <div className="p-5 rounded-lg text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: '#a4262c' }}>
          Could not fetch pricing. Check your inputs and try again.
        </div>
      )}
      {result && !isLoading && (
        <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <div className="flex items-baseline justify-between">
            <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Estimated monthly cost (PAYG)</h2>
            <span className="text-[28px] font-bold" style={{ color: '#0078d4' }}>£{fmt(result.totalMonthly)}</span>
          </div>

          <div className="space-y-2 pt-2" style={{ borderTop: '1px solid var(--outline-variant)' }}>
            {result.controlPlaneMonthly > 0 && (
              <div className="flex justify-between text-[13px]">
                <span style={{ color: 'var(--text-secondary)' }}>Control plane ({TIER_LABELS[tier]})</span>
                <span style={{ color: 'var(--text-primary)' }}>£{fmt(result.controlPlaneMonthly)}</span>
              </div>
            )}
            {result.uptimeSlaMonthly > 0 && (
              <div className="flex justify-between text-[13px]">
                <span style={{ color: 'var(--text-secondary)' }}>Uptime SLA (add-on)</span>
                <span style={{ color: 'var(--text-primary)' }}>£{fmt(result.uptimeSlaMonthly)}</span>
              </div>
            )}
            {result.nodePools.map((p, i) => (
              <div key={i} className="flex justify-between text-[13px]">
                <span style={{ color: 'var(--text-secondary)' }}>
                  {p.sku} × {p.nodeCount} node{p.nodeCount !== 1 ? 's' : ''}{p.spot ? ' (Spot)' : ''}
                </span>
                <span style={{ color: 'var(--text-primary)' }}>£{fmt(p.monthly)}</span>
              </div>
            ))}
            {result.lbEgressMonthly > 0 && (
              <div className="flex justify-between text-[13px]">
                <span style={{ color: 'var(--text-secondary)' }}>Load Balancer egress ({egressGb} GB)</span>
                <span style={{ color: 'var(--text-primary)' }}>£{fmt(result.lbEgressMonthly)}</span>
              </div>
            )}
          </div>

          {result.pricesLastUpdated && (
            <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
              Node pool rates from D1 — last updated {result.pricesLastUpdated.split(' ')[0]}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <ShareButton inputs={inputs} result={result} service="aks" />
          </div>

          <a href="https://azure.microsoft.com/en-gb/pricing/details/kubernetes-service/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono mt-1" style={{ color: 'var(--text-tertiary)' }}>
            <span className="material-symbols-outlined text-[13px]">open_in_new</span>
            Verify on official Azure pricing page
          </a>

          <p className="text-[11px] pt-1" style={{ color: 'var(--text-tertiary)' }}>{result.disclaimer}</p>
        </div>
      )}

      {/* Billing traps callout */}
      <div className="p-5 rounded-lg" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
          <span className="material-symbols-outlined text-[#e8b000] text-[16px] align-middle mr-1">warning</span>
          AKS billing traps
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              title: 'Control plane upgrade',
              body: 'Free → Standard is £55.10/month per cluster hidden fee. Across 5 environments: £275/month before a single node.',
            },
            {
              title: 'Uptime SLA is an add-on',
              body: 'Uptime SLA does not replace the Standard tier fee — it adds to it. Standard + Uptime SLA = £110.20/month per cluster.',
            },
            {
              title: 'Spot node interruption',
              body: 'Azure can evict Spot nodes with 30 seconds notice. Avoid Spot for stateful workloads or anything requiring guaranteed availability.',
            },
            {
              title: 'Hidden Load Balancer egress',
              body: 'AKS provisions a Standard LB by default. Outbound rule data processing charges (£0.0037/GB) do not appear on the AKS pricing page.',
            },
          ].map(trap => (
            <div key={trap.title} className="p-3 rounded" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{trap.title}</p>
              <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{trap.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-2">
        <Link
          to="/guides/azure-aks-pricing-uk"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>menu_book</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure AKS Pricing Guide UK</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Control plane traps, Spot nodes, Uptime SLA, egress, and Reserved Instance strategy</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
      </div>
    </div>
  )
}
